import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { PageTransition } from './components/PageTransition';
import { AuthProvider } from './contexts/AuthContext';
import { ProdAuthProvider } from './contexts/ProdAuthContext';
import { BuyerAuthProvider } from './contexts/BuyerAuthContext';
import { ToastProvider } from './components/ToastContainer';
import { LoadingSpinner } from './components/LoadingSpinner';
import Modal from './components/Modal';
import { OrganizationSchema } from './components/StructuredData';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Coffees } from './pages/Coffees';
import { CoffeeDetail } from './pages/CoffeeDetail';
import { Contact } from './pages/Contact';
import { InquirePage } from './pages/InquirePage';
import { Nursery } from './pages/Nursery';
import { SamplePackPage } from './pages/SamplePackPage';

// Lazy load admin and form components
const SampleOrderForm = lazy(() => import('./components/SampleOrderForm'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import('./pages/admin/Dashboard').then(m => ({ default: m.Dashboard })));
const CoffeeList = lazy(() => import('./pages/admin/CoffeeList').then(m => ({ default: m.CoffeeList })));
const CoffeeForm = lazy(() => import('./pages/admin/CoffeeForm').then(m => ({ default: m.CoffeeForm })));
const OrderList = lazy(() => import('./pages/admin/OrderList').then(m => ({ default: m.OrderList })));
const InquiryList = lazy(() => import('./pages/admin/InquiryList').then(m => ({ default: m.InquiryList })));
const InvoiceList = lazy(() => import('./pages/admin/InvoiceList').then(m => ({ default: m.InvoiceList })));
const InvoiceForm = lazy(() => import('./pages/admin/InvoiceForm').then(m => ({ default: m.InvoiceForm })));
const BuyerList = lazy(() => import('./pages/admin/BuyerList').then(m => ({ default: m.BuyerList })));
const BuyerForm = lazy(() => import('./pages/admin/BuyerForm').then(m => ({ default: m.BuyerForm })));
const ContractList = lazy(() => import('./pages/admin/ContractList').then(m => ({ default: m.ContractList })));
const ContractForm = lazy(() => import('./pages/admin/ContractForm').then(m => ({ default: m.ContractForm })));
const OfferList = lazy(() => import('./pages/admin/OfferList').then(m => ({ default: m.OfferList })));
const OfferForm = lazy(() => import('./pages/admin/OfferForm').then(m => ({ default: m.OfferForm })));
const ChangeRequestList = lazy(() => import('./pages/admin/ChangeRequestList').then(m => ({ default: m.ChangeRequestList })));
const ExportInvoiceList = lazy(() => import('./pages/admin/ExportInvoiceList').then(m => ({ default: m.ExportInvoiceList })));
const ExportInvoiceForm = lazy(() => import('./pages/admin/ExportInvoiceForm').then(m => ({ default: m.ExportInvoiceForm })));
const PackingListList = lazy(() => import('./pages/admin/PackingListList').then(m => ({ default: m.PackingListList })));
const PackingListForm = lazy(() => import('./pages/admin/PackingListForm').then(m => ({ default: m.PackingListForm })));

// Production management system
const ProductionLayout = lazy(() => import('./pages/production/ProductionLayout').then(m => ({ default: m.ProductionLayout })));
const ProductionDashboard = lazy(() => import('./pages/production/ProductionDashboard').then(m => ({ default: m.ProductionDashboard })));
const FarmerList = lazy(() => import('./pages/production/FarmerList').then(m => ({ default: m.FarmerList })));
const ProductionLoginPage = lazy(() => import('./pages/production/ProductionLogin').then(m => ({ default: m.ProductionLogin })));
const ForgotPassword = lazy(() => import('./pages/production/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/production/ResetPassword').then(m => ({ default: m.ResetPassword })));

// Buyer portal
const PortalLogin = lazy(() => import('./pages/PortalLogin').then(m => ({ default: m.PortalLogin })));
const Portal = lazy(() => import('./pages/Portal').then(m => ({ default: m.Portal })));
const DeliveryList = lazy(() => import('./pages/production/DeliveryList').then(m => ({ default: m.DeliveryList })));
const DeliveryForm = lazy(() => import('./pages/production/DeliveryForm').then(m => ({ default: m.DeliveryForm })));
const BatchList = lazy(() => import('./pages/production/BatchList').then(m => ({ default: m.BatchList })));
const BatchForm = lazy(() => import('./pages/production/BatchForm').then(m => ({ default: m.BatchForm })));
const BatchDetail = lazy(() => import('./pages/production/BatchDetail').then(m => ({ default: m.BatchDetail })));
const TripList = lazy(() => import('./pages/production/TripList').then(m => ({ default: m.TripList })));
const InventoryView = lazy(() => import('./pages/production/InventoryView').then(m => ({ default: m.InventoryView })));
const UserList = lazy(() => import('./pages/production/UserList').then(m => ({ default: m.UserList })));
const CentreList = lazy(() => import('./pages/production/CentreList').then(m => ({ default: m.CentreList })));
const ReportsView = lazy(() => import('./pages/production/ReportsView').then(m => ({ default: m.ReportsView })));
const DryingBedsView = lazy(() => import('./pages/production/DryingBedsView').then(m => ({ default: m.DryingBedsView })));
const HubStaffCRM = lazy(() => import('./pages/production/HubStaffCRM').then(m => ({ default: m.HubStaffCRM })));

// Sample Order Modal Context
interface SampleOrderModalContextValue {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const SampleOrderModalContext = React.createContext<SampleOrderModalContextValue | undefined>(undefined);

export const useSampleOrderModal = () => {
  const context = React.useContext(SampleOrderModalContext);
  if (!context) {
    throw new Error('useSampleOrderModal must be used within App');
  }
  return context;
};

const App: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About' },
    { href: '/coffees', label: 'Coffees' },
    { href: '/sample-pack', label: 'Samples' },
    { href: '/nursery', label: 'Nursery' },
  ];

  return (
    <ToastProvider>
      <AuthProvider>
          <ProdAuthProvider>
          <BuyerAuthProvider>
          <SampleOrderModalContext.Provider value={{ isOpen: isModalOpen, openModal, closeModal }}>
            <Router>
              <ScrollToTop />
              <OrganizationSchema />
              <Routes>
                {/* Buyer portal routes — separate auth, no Header/Footer */}
                <Route path="/portal/login" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <PortalLogin />
                  </Suspense>
                } />
                <Route path="/portal" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <Portal />
                  </Suspense>
                } />

                {/* Production routes — separate auth, no Header/Footer */}
                <Route path="/production/login" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <ProductionLoginPage />
                  </Suspense>
                } />
                <Route path="/production/forgot-password" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <ForgotPassword />
                  </Suspense>
                } />
                <Route path="/production/reset-password" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <ResetPassword />
                  </Suspense>
                } />
                <Route path="/production" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <ProductionLayout />
                  </Suspense>
                }>
                  <Route index element={<Suspense fallback={null}><ProductionDashboard /></Suspense>} />
                  <Route path="farmers" element={<Suspense fallback={null}><FarmerList /></Suspense>} />
                  <Route path="deliveries" element={<Suspense fallback={null}><DeliveryList /></Suspense>} />
                  <Route path="deliveries/new" element={<Suspense fallback={null}><DeliveryForm /></Suspense>} />
                  <Route path="batches" element={<Suspense fallback={null}><BatchList /></Suspense>} />
                  <Route path="batches/new" element={<Suspense fallback={null}><BatchForm /></Suspense>} />
                  <Route path="batches/:id" element={<Suspense fallback={null}><BatchDetail /></Suspense>} />
                  <Route path="trips" element={<Suspense fallback={null}><TripList /></Suspense>} />
                  <Route path="inventory" element={<Suspense fallback={null}><InventoryView /></Suspense>} />
                  <Route path="users" element={<Suspense fallback={null}><UserList /></Suspense>} />
                  <Route path="centres" element={<Suspense fallback={null}><CentreList /></Suspense>} />
                  <Route path="drying" element={<Suspense fallback={null}><DryingBedsView /></Suspense>} />
                  <Route path="reports" element={<Suspense fallback={null}><ReportsView /></Suspense>} />
                  <Route path="hub-staff" element={<Suspense fallback={null}><HubStaffCRM /></Suspense>} />
                </Route>

                {/* Admin routes — no Header/Footer */}
                <Route path="/admin" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="md" /></div>}>
                    <AdminLayout />
                  </Suspense>
                }>
                  <Route index element={<Suspense fallback={null}><Dashboard /></Suspense>} />
                  <Route path="coffees" element={<Suspense fallback={null}><CoffeeList /></Suspense>} />
                  <Route path="coffees/new" element={<Suspense fallback={null}><CoffeeForm /></Suspense>} />
                  <Route path="coffees/:id" element={<Suspense fallback={null}><CoffeeForm /></Suspense>} />
                  <Route path="orders" element={<Suspense fallback={null}><OrderList /></Suspense>} />
                  <Route path="inquiries" element={<Suspense fallback={null}><InquiryList /></Suspense>} />
                  <Route path="invoices" element={<Suspense fallback={null}><InvoiceList /></Suspense>} />
                  <Route path="invoices/new" element={<Suspense fallback={null}><InvoiceForm /></Suspense>} />
                  <Route path="invoices/:id" element={<Suspense fallback={null}><InvoiceForm /></Suspense>} />
                  <Route path="buyers" element={<Suspense fallback={null}><BuyerList /></Suspense>} />
                  <Route path="buyers/new" element={<Suspense fallback={null}><BuyerForm /></Suspense>} />
                  <Route path="buyers/:id" element={<Suspense fallback={null}><BuyerForm /></Suspense>} />
                  <Route path="contracts" element={<Suspense fallback={null}><ContractList /></Suspense>} />
                  <Route path="contracts/new" element={<Suspense fallback={null}><ContractForm /></Suspense>} />
                  <Route path="contracts/:id" element={<Suspense fallback={null}><ContractForm /></Suspense>} />
                  <Route path="offers" element={<Suspense fallback={null}><OfferList /></Suspense>} />
                  <Route path="offers/new" element={<Suspense fallback={null}><OfferForm /></Suspense>} />
                  <Route path="offers/:id" element={<Suspense fallback={null}><OfferForm /></Suspense>} />
                  <Route path="change-requests" element={<Suspense fallback={null}><ChangeRequestList /></Suspense>} />
                  <Route path="export-invoices" element={<Suspense fallback={null}><ExportInvoiceList /></Suspense>} />
                  <Route path="export-invoices/new" element={<Suspense fallback={null}><ExportInvoiceForm /></Suspense>} />
                  <Route path="export-invoices/:id" element={<Suspense fallback={null}><ExportInvoiceForm /></Suspense>} />
                  <Route path="packing-lists" element={<Suspense fallback={null}><PackingListList /></Suspense>} />
                  <Route path="packing-lists/new" element={<Suspense fallback={null}><PackingListForm /></Suspense>} />
                  <Route path="packing-lists/:id" element={<Suspense fallback={null}><PackingListForm /></Suspense>} />
                  {/* Production pages embedded in admin — no second login required */}
                  <Route path="production" element={<Suspense fallback={null}><ProductionDashboard /></Suspense>} />
                  <Route path="production/farmers" element={<Suspense fallback={null}><FarmerList /></Suspense>} />
                  <Route path="production/deliveries" element={<Suspense fallback={null}><DeliveryList /></Suspense>} />
                  <Route path="production/deliveries/new" element={<Suspense fallback={null}><DeliveryForm /></Suspense>} />
                  <Route path="production/batches" element={<Suspense fallback={null}><BatchList /></Suspense>} />
                  <Route path="production/batches/new" element={<Suspense fallback={null}><BatchForm /></Suspense>} />
                  <Route path="production/batches/:id" element={<Suspense fallback={null}><BatchDetail /></Suspense>} />
                  <Route path="production/trips" element={<Suspense fallback={null}><TripList /></Suspense>} />
                  <Route path="production/inventory" element={<Suspense fallback={null}><InventoryView /></Suspense>} />
                  <Route path="production/users" element={<Suspense fallback={null}><UserList /></Suspense>} />
                  <Route path="production/centres" element={<Suspense fallback={null}><CentreList /></Suspense>} />
                  <Route path="production/drying" element={<Suspense fallback={null}><DryingBedsView /></Suspense>} />
                  <Route path="production/reports" element={<Suspense fallback={null}><ReportsView /></Suspense>} />
                </Route>

                {/* Public site */}
                <Route path="/*" element={
                  <>
                    <a href="#main-content" className="skip-to-content">
                      Skip to main content
                    </a>
                    <div className="min-h-screen flex flex-col bg-light-bg text-light-text-primary antialiased">
                      <Header navLinks={navLinks} />
                      <main id="main-content" className="flex-grow">
                        <PageTransition>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/coffees" element={<Coffees />} />
                            <Route path="/coffee/:slug" element={<CoffeeDetail />} />
                            <Route path="/sample-pack" element={<SamplePackPage />} />
                            <Route path="/nursery" element={<Nursery />} />
                            <Route path="/inquire" element={<InquirePage />} />
                            <Route path="/contact" element={<Contact />} />
                          </Routes>
                        </PageTransition>
                      </main>
                      <Footer />

                      {/* Sample Order Modal */}
                      <Modal isOpen={isModalOpen} onClose={closeModal}>
                        <Suspense fallback={
                          <div className="flex items-center justify-center p-8">
                            <LoadingSpinner size="md" text="Loading form..." />
                          </div>
                        }>
                          <SampleOrderForm
                            onSuccess={closeModal}
                            onCancel={closeModal}
                          />
                        </Suspense>
                      </Modal>
                    </div>
                  </>
                } />
              </Routes>
            </Router>
          </SampleOrderModalContext.Provider>
          </BuyerAuthProvider>
          </ProdAuthProvider>
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
